import moreStyleImg from "@/assets/images/art-style-more.png";
import GreyLogo from "@/assets/images/grey-logo.svg";
import { BlurBackgroundContainer } from "@/component/BlurBackgroundContainer";
import { ArtifactsViewerData } from "@/hooks/useArtifactsViewerData";
import { convertFilePathToUrl } from "@/util/convertFilePathToUrl";
import { Image } from "antd";
import classNames from "classnames";
import isEqual from "fast-deep-equal";
import React, { useCallback, useMemo, useState } from "react";
import { Section } from "./Section";

type ArtStyleSectionProps = ArtifactsViewerData["artStyle"];

interface StyleItemProps {
  name: string;
  image: string;
  isSelected: boolean;
  tags: string[];
  enablePreview?: boolean;
}

function StyleItem(props: StyleItemProps) {
  const { name, image, isSelected, enablePreview, tags } = props;
  const [hasError, setHasError] = useState(false);
  const originalImageUrl = convertFilePathToUrl(image);
  const compressedImageUrl = convertFilePathToUrl(image, {
    level: "medium",
  });

  // 缓存 previewConfig 对象
  const previewConfig = useMemo(
    () => ({
      mask: null,
      src: originalImageUrl,
    }),
    [originalImageUrl]
  );

  // 缓存 onError 回调
  const handleError = useCallback(() => {
    setHasError(true);
  }, []);

  // 缓存 alt 文本
  const altText = useMemo(() => name || "Art Style", [name]);

  return (
    <div
      className={classNames(
        "artifacts-grid-item overflow-hidden group p-3  hover:shadow-none! transition-none! cursor-default!",
        isSelected ? "border-2! border-primary!" : "hover:border-transparent!"
      )}
    >
      <div
        className={classNames(
          "aspect-video rounded-lg overflow-hidden w-full relative",
          {
            "cursor-pointer": enablePreview,
          }
        )}
      >
        {originalImageUrl && !hasError ? (
          <BlurBackgroundContainer
            mediaType="image"
            src={compressedImageUrl}
            previewSrc={compressedImageUrl}
            previewConfig={previewConfig}
            height="100%"
            alt={altText}
            className="w-full h-full"
            onError={handleError}
            enablePreview={enablePreview}
          />
        ) : (
          <div className="w-full h-full object-cover rounded-lg flex items-center justify-center">
            <GreyLogo className="w-16 h-16 text-text-secondary" />
          </div>
        )}
      </div>
      {name && (
        <p className="mt-3 text-text-primary text-base font-bold leading-normal">
          {name}
        </p>
      )}
      {tags.length > 0 && (
        <div className="text-xs font-normal leading-normal text-text-tertiary line-clamp-4">
          {tags.join(", ")}
        </div>
      )}
    </div>
  );
}

export const ArtStyleSection = React.memo(
  function ArtStyleSection(props: ArtStyleSectionProps) {
    const { id, data, visible, title } = props;

    if (!visible || !data?.styles || data.styles.length === 0) {
      return null;
    }

    console.log("data", data);

    const selectedName = data.selected?.name;

    return (
      <Section id={id} title={title}>
        <div className="artifacts-grid grid-cols-3_4">
          <Image.PreviewGroup>
            {data.styles.map((style, index) => (
              <StyleItem
                key={`art-style-${style.name || index}`}
                name={style.name}
                image={style.image}
                isSelected={selectedName === style.name}
                tags={style.tags}
                enablePreview
              />
            ))}
          </Image.PreviewGroup>

          <StyleItem
            key="art-style-custom"
            name="Tell Me in the Chat"
            image={moreStyleImg.src}
            isSelected={false}
            tags={[]}
          />
        </div>
      </Section>
    );
  },
  (prevProps, nextProps) => {
    return isEqual(prevProps, nextProps);
  }
);
